/**
 * AppLayout Component
 * Coláiste Lurgan Management System - Main application layout with navigation
 */

import { useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth, useUserPermissions } from '../../hooks/useAuth';
import { Button } from '../ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import { LanguageSwitcher } from '../ui/language-switcher';
import { Navigation } from './Navigation';
import { Menu, X, User, HelpCircle } from 'lucide-react';
import { ReactComponent as BainisteoirLogo } from '../../assets/logos-svg/Bainisteoir-Lurgan-Vector.svg';
import { ReactComponent as RacketLogoColored } from '../../assets/logos-svg/logo-w-text-c.svg';
import { ReactComponent as RacketLogoWhite } from '../../assets/logos-svg/logo-w-text-w.svg';
import { ImpersonationBanner } from './ImpersonationBanner';
import { CookieBanner } from '../gdpr/CookieBanner';
import { CookieSettings } from '../gdpr/CookieSettings';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { Link } from 'react-router-dom';

export function AppLayout() {
  const { t } = useTranslation();
  const { user, logout, isLoggingOut } = useAuth();
  const { userType } = useUserPermissions();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mediumMenuExpanded, setMediumMenuExpanded] = useState(false);
  const {
    hasConsented,
    functional,
    settingsOpen,
    openSettings,
    closeSettings,
    acceptAll,
    rejectNonEssential,
    savePreferences,
  } = useCookieConsent();

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  return (
    <>
    <div className="min-h-screen bg-background flex flex-col">
      <ImpersonationBanner />
      {/* Top Header */}
      <header className="bg-card px-4 py-4 relative z-40">
        <div className="flex items-center justify-between">
          {/* Logo and Title Section */}
          <div className="flex items-center space-x-4">
            {/* Mobile hamburger menu */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            {/* Medium screen hamburger menu */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="hidden lg:block xl:hidden"
              onClick={() => setMediumMenuExpanded(!mediumMenuExpanded)}
            >
              <Menu className="h-5 w-5" />
            </Button>
            
            <div>
              <BainisteoirLogo
                className="h-8 w-auto text-black dark:text-white"
                aria-label="Bainisteoir"
              />
            </div>
          </div>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* User Info - Hidden on small screens */}
            <div className="hidden sm:block text-right">
              <p className="text-sm font-medium">
                {user?.first_name || user?.last_name
                  ? `${user.first_name} ${user.last_name}`.trim()
                  : user?.username || user?.email}
              </p>
              <p className="text-xs text-muted-foreground">
                {t(`userType.${userType}`)}
              </p>
            </div>

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium text-gray-900" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }}>
                    {user?.first_name?.[0] || user?.username?.[0] || '?'}
                    {user?.first_name && user?.last_name ? user.last_name[0] : ''}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">
                    {user?.first_name || user?.last_name
                      ? `${user.first_name} ${user.last_name}`.trim()
                      : user?.username || user?.email}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {user?.email}
                  </p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  {t('nav.profile', 'Profile')}
                </DropdownMenuItem>
                <DropdownMenuItem className="flex items-center gap-2">
                  <HelpCircle className="h-4 w-4" />
                  {t('nav.help', 'Help')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  disabled={isLoggingOut}
                  className="text-destructive focus:text-destructive"
                >
                  {isLoggingOut ? (
                    <div className="flex items-center gap-2">
                      <div className="animate-spin rounded-full h-3 w-3 border-b border-current"></div>
                      <span>{t('nav.loggingOut')}</span>
                    </div>
                  ) : (
                    t('nav.logout')
                  )}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        {/* Gradient accent line */}
        <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'linear-gradient(135deg, #F6BD66, #F28482, #F5CAC3)' }} />
      </header>

      {/* Main Layout Below Header */}
      <div className="flex flex-1">
        {/* Responsive Sidebar */}
        <div className={`
          bg-card border-r transition-all duration-200 ease-in-out
          fixed top-[theme(spacing.20)] bottom-0 left-0 z-30
          lg:static lg:top-0
          ${
            // Mobile behavior
            mobileMenuOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64'
          }
          ${
            // Desktop behavior  
            mediumMenuExpanded 
              ? 'xl:w-64 lg:w-64'  // Medium expanded: show full width
              : 'xl:w-64 lg:w-16'  // Medium collapsed: show icon-only
          }
          lg:translate-x-0
        `}>
          {/* Mobile close button */}
          <div className="lg:hidden p-2 border-b">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setMobileMenuOpen(false)}
              className="ml-auto flex"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto">
            <Navigation mediumExpanded={mediumMenuExpanded} onMobileNavClick={() => setMobileMenuOpen(false)} />
          </div>
        </div>

        {/* Mobile overlay */}
        {mobileMenuOpen && (
          <div 
            className="fixed inset-0 z-20 bg-black/20 lg:hidden" 
            onClick={() => setMobileMenuOpen(false)}
          />
        )}
        
        {/* Medium screen overlay (when sidebar is expanded) */}
        {mediumMenuExpanded && (
          <div 
            className="fixed inset-0 z-20 bg-black/20 hidden lg:block xl:hidden" 
            onClick={() => setMediumMenuExpanded(false)}
          />
        )}

        {/* Main Content */}
        <main className="flex-1 min-w-0 p-3 sm:p-6 ml-0 lg:ml-0 flex flex-col" style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}>
          <div className="flex-1">
            <Outlet />
          </div>
          <footer className="mt-8 pt-4 border-t border-border flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Link to="/privacy-policy" className="hover:text-foreground transition-colors">
                {t('gdpr.footer.privacyPolicy')}
              </Link>
              <button
                onClick={openSettings}
                className="hover:text-foreground transition-colors"
              >
                {t('gdpr.footer.cookieSettings')}
              </button>
            </div>
            <a
              href="https://racketsoftware.ie"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group"
            >
              <span className="text-xs">{t('footer.builtBy')}</span>
              <RacketLogoColored className="h-8 w-auto dark:hidden" />
              <RacketLogoWhite className="h-8 w-auto hidden dark:block" />
            </a>
          </footer>
        </main>
      </div>
    </div>

    {!hasConsented && (
      <CookieBanner
        onAcceptAll={acceptAll}
        onReject={rejectNonEssential}
        onOpenSettings={openSettings}
      />
    )}
    <CookieSettings
      open={settingsOpen}
      onClose={closeSettings}
      onSave={savePreferences}
      onAcceptAll={acceptAll}
      onRejectAll={rejectNonEssential}
      initialFunctional={functional}
    />
    </>
  );
}

export default AppLayout;